// src/pages/admin/StartupsAdminPage.tsx
// Startup programme set-up for admins: yearly cohorts, the known accelerator
// list the application form offers, and who holds the programme officer role.
import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import Button from '@/components/ui/Button';
import { getErrorMessage } from '@/lib/api';
import { formatDay, startupsAdminApi } from '@/lib/startups';
import { PageHeader } from '@/components/ui/PageHeader';
import { Panel } from '@/components/ui/Panel';
import { Notice } from '@/components/ui/Notice';
import { SkeletonLines } from '@/components/ui/Skeleton';
import { checkbox, fieldControl, fieldLabel } from '@/components/ui/fieldStyles';

const inputCls = fieldControl;
/** "2027-03-01" (a date input) -> seconds at 00:00 UTC. */
const dayToSeconds = (v: string) => Math.floor(Date.parse(`${v}T00:00:00Z`) / 1000);
/** The closing day stays open to its last second (UTC), so "closes 31 March" means all of 31 March. */
const endOfDay = (v: string) => dayToSeconds(v) + 86_399;

const StartupsAdminPage: React.FC = () => {
  const qc = useQueryClient();
  const cohorts = useQuery({ queryKey: ['admin-startup-cohorts'], queryFn: startupsAdminApi.cohorts });
  const accelerators = useQuery({ queryKey: ['admin-startup-accelerators'], queryFn: startupsAdminApi.accelerators });
  const officers = useQuery({ queryKey: ['admin-startup-officers'], queryFn: startupsAdminApi.officers });

  const [name, setName] = useState('');
  const [opens, setOpens] = useState('');
  const [closes, setCloses] = useState('');
  const [cohortError, setCohortError] = useState<string | null>(null);
  const [accName, setAccName] = useState('');
  const [officerEmail, setOfficerEmail] = useState('');

  const onError = (e: unknown) => toast.error(getErrorMessage(e));
  const createCohort = useMutation({
    mutationFn: () => startupsAdminApi.saveCohort({ name: name.trim(), opens_at: dayToSeconds(opens), closes_at: endOfDay(closes) }),
    onSuccess: () => { toast.success('Cohort created'); setName(''); setOpens(''); setCloses(''); qc.invalidateQueries({ queryKey: ['admin-startup-cohorts'] }); },
    onError: (e) => setCohortError(getErrorMessage(e)),
  });
  const addAcc = useMutation({
    mutationFn: () => startupsAdminApi.addAccelerator(accName.trim()),
    onSuccess: () => { setAccName(''); qc.invalidateQueries({ queryKey: ['admin-startup-accelerators'] }); },
    onError,
  });
  const toggleAcc = useMutation({
    mutationFn: (v: { id: string; active: boolean }) => startupsAdminApi.setAccelerator(v.id, v.active),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-startup-accelerators'] }),
    onError,
  });
  const addOfficer = useMutation({
    mutationFn: () => startupsAdminApi.addOfficer(officerEmail.trim()),
    onSuccess: () => { toast.success('Programme officer added. They see the change when they next sign in.'); setOfficerEmail(''); qc.invalidateQueries({ queryKey: ['admin-startup-officers'] }); },
    onError,
  });
  const removeOfficer = useMutation({
    mutationFn: (id: string) => startupsAdminApi.removeOfficer(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-startup-officers'] }),
    onError,
  });

  const submitCohort = (e: React.FormEvent) => {
    e.preventDefault();
    setCohortError(null);
    if (!name.trim() || !opens || !closes) return setCohortError('Give the cohort a name and both dates.');
    if (dayToSeconds(closes) <= dayToSeconds(opens)) return setCohortError('A cohort must close after it opens.');
    createCohort.mutate();
  };

  return (
    <div className="space-y-6">
      <PageHeader title="Startups" description="Cohorts, the accelerator list and programme officers for the startup programme." />

      <Panel title="Cohorts" bodyClassName="space-y-5">
        {cohorts.isLoading ? (
          <SkeletonLines count={2} lineClassName="h-6" />
        ) : (
          <ul className="divide-y divide-border rounded-xl border border-border text-sm">
            {cohorts.data?.cohorts.map(c => (
              <li key={c.id} className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 px-4 py-3">
                <span className="font-medium text-foreground">{c.name}</span>
                <span className="text-muted-foreground">{formatDay(c.opens_at)} to {formatDay(c.closes_at)}</span>
              </li>
            ))}
            {cohorts.data?.cohorts.length === 0 && <li className="px-4 py-3 text-muted-foreground">No cohorts yet. Create the first one below.</li>}
          </ul>
        )}
        <form onSubmit={submitCohort} className="grid gap-3 sm:grid-cols-[1fr_10rem_10rem_auto] sm:items-end">
          <div>
            <label htmlFor="cohort-name" className={fieldLabel}>Cohort name</label>
            <input id="cohort-name" className={inputCls} value={name} onChange={e => setName(e.target.value)} placeholder="2027" />
          </div>
          <div>
            <label htmlFor="cohort-opens" className={fieldLabel}>Opens</label>
            <input id="cohort-opens" type="date" className={inputCls} value={opens} onChange={e => setOpens(e.target.value)} />
          </div>
          <div>
            <label htmlFor="cohort-closes" className={fieldLabel}>Closes</label>
            <input id="cohort-closes" type="date" className={inputCls} value={closes} onChange={e => setCloses(e.target.value)} />
          </div>
          <Button type="submit" className="h-11" disabled={createCohort.isPending}>Create cohort</Button>
        </form>
        {cohortError && <Notice tone="error">{cohortError}</Notice>}
      </Panel>

      <Panel title="Known accelerators" bodyClassName="space-y-5">
        {accelerators.isLoading ? (
          <SkeletonLines count={3} lineClassName="h-6" />
        ) : accelerators.data?.accelerators.length ? (
          <ul className="grid gap-2 text-sm sm:grid-cols-2">
            {accelerators.data.accelerators.map(acc => (
              <li key={acc.id} className="flex items-center gap-2.5">
                <input type="checkbox" id={`acc-${acc.id}`} className={checkbox} checked={!!acc.active}
                  onChange={e => toggleAcc.mutate({ id: acc.id, active: e.target.checked })} />
                <label htmlFor={`acc-${acc.id}`} className={acc.active ? 'text-foreground' : 'text-muted-foreground line-through'}>{acc.name}</label>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-muted-foreground">No accelerators yet. Ones you add appear on the application form.</p>
        )}
        <form onSubmit={e => { e.preventDefault(); if (accName.trim().length >= 2) addAcc.mutate(); }} className="flex flex-wrap items-end gap-3">
          <div className="min-w-48 flex-1">
            <label htmlFor="acc-name" className={fieldLabel}>Add an accelerator</label>
            <input id="acc-name" className={inputCls} value={accName} onChange={e => setAccName(e.target.value)} />
          </div>
          <Button type="submit" variant="outline" className="h-11" disabled={addAcc.isPending}>Add</Button>
        </form>
      </Panel>

      <Panel title="Programme officers" bodyClassName="space-y-5">
        <p className="text-sm text-muted-foreground">Officers can read applications and approve or reject them. Nothing else in the admin area.</p>
        {officers.isLoading ? (
          <SkeletonLines count={2} />
        ) : (
          <ul className="divide-y divide-border rounded-xl border border-border text-sm">
            {officers.data?.officers.map(o => (
              <li key={o.user_id} className="flex items-center justify-between gap-3 px-4 py-2">
                <span className="min-w-0 truncate text-foreground">{o.email}{o.full_name && <span className="text-muted-foreground"> · {o.full_name}</span>}</span>
                <Button variant="ghost" size="sm" onClick={() => removeOfficer.mutate(o.user_id)}>Remove</Button>
              </li>
            ))}
            {officers.data?.officers.length === 0 && <li className="px-4 py-3 text-muted-foreground">No programme officers yet.</li>}
          </ul>
        )}
        <form onSubmit={e => { e.preventDefault(); if (officerEmail.trim()) addOfficer.mutate(); }} className="flex flex-wrap items-end gap-3">
          <div className="min-w-48 flex-1">
            <label htmlFor="officer-email" className={fieldLabel}>Officer email</label>
            <input id="officer-email" type="email" className={inputCls} value={officerEmail} onChange={e => setOfficerEmail(e.target.value)} />
          </div>
          <Button type="submit" variant="outline" className="h-11" disabled={addOfficer.isPending}>Add officer</Button>
        </form>
      </Panel>
    </div>
  );
};

export default StartupsAdminPage;
